import { sql } from "drizzle-orm";
import {
  check,
  foreignKey,
  index,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, users } from "./schemaBase.js";

export const ACTION_CENTRE_QUEUES = [
  "requests",
  "clashes",
  "exceptions",
  "credits",
  "waitlist",
  "unmatched",
  "student-reviews",
  "bugs",
] as const;
export type ActionCentreQueue = (typeof ACTION_CENTRE_QUEUES)[number];

/**
 * Per-account acknowledgement of Action Centre work.
 *
 * The underlying queue row remains unresolved and visible. This table only
 * records that one staff account has reviewed a particular current item, so a
 * navigation badge can describe what is new to that person instead of the
 * lifetime size of the operational queue.
 */
export const actionCentreAcknowledgements = pgTable(
  "action_centre_acknowledgements",
  {
    id: varchar("id")
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    organisationId: varchar("organisation_id").notNull(),
    accountId: varchar("account_id").notNull(),
    queue: text("queue").notNull(),
    itemKey: text("item_key").notNull(),
    acknowledgedAt: timestamp("acknowledged_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    foreignKey({
      columns: [t.organisationId],
      foreignColumns: [organisations.id],
      name: "action_centre_ack_organisation_id_organisations_id_fk",
    })
      .onDelete("restrict")
      .onUpdate("no action"),
    foreignKey({
      columns: [t.accountId],
      foreignColumns: [users.id],
      name: "action_centre_ack_account_id_users_id_fk",
    })
      .onDelete("cascade")
      .onUpdate("no action"),
    index("action_centre_ack_account_idx").on(t.organisationId, t.accountId, t.queue),
    uniqueIndex("action_centre_ack_item_idx").on(
      t.organisationId,
      t.accountId,
      t.queue,
      t.itemKey,
    ),
    check(
      "action_centre_ack_queue_check",
      sql`${t.queue} in ('requests', 'clashes', 'exceptions', 'credits', 'waitlist', 'unmatched', 'student-reviews', 'bugs')`,
    ),
  ],
);
